import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { Delivery } from '../../types';

const DELIVERY_STEPS = [
  {
    id: 'scheduled',
    label: 'Scheduled',
    description: 'Delivery planned',
  },
  {
    id: 'in-progress',
    label: 'Receiving',
    description: 'Items being checked',
  },
  {
    id: 'completed',
    label: 'Completed',
    description: 'All items received',
  },
];

type StepStatus = 'complete' | 'current' | 'upcoming';

function getStepStatus(
  deliveryStatus: Delivery['status'],
  stepId: string
): StepStatus {
  const statusOrder = ['scheduled', 'in-progress', 'completed'];
  const stepOrder = ['scheduled', 'in-progress', 'completed'];

  if (deliveryStatus === 'rejected') {
    if (stepId === 'scheduled') return 'complete';
    if (stepId === 'in-progress') return 'current';
    return 'upcoming';
  }

  if (deliveryStatus === 'partial') {
    if (stepId === 'completed') return 'current';
    return 'complete';
  }

  const currentIndex = statusOrder.indexOf(deliveryStatus);
  const stepIndex = stepOrder.indexOf(stepId);

  if (stepIndex < currentIndex) return 'complete';
  if (stepIndex === currentIndex) return 'current';
  return 'upcoming';
}

function getStepIcon(
  stepStatus: StepStatus,
  status: Delivery['status'],
  index: number
) {
  if (stepStatus === 'complete') {
    return <CheckCircle className="h-5 w-5" />;
  }
  if (stepStatus === 'current') {
    if (status === 'rejected') return <XCircle className="h-5 w-5" />;
    if (status === 'partial') return <AlertCircle className="h-5 w-5" />;
    return <Clock className="h-5 w-5" />;
  }
  return <span className="text-sm font-medium">{index + 1}</span>;
}

function getStepClassName(
  stepStatus: StepStatus,
  status: Delivery['status']
) {
  if (stepStatus === 'complete') {
    return 'border-green-500 bg-green-500 text-white';
  }
  if (stepStatus === 'current') {
    if (status === 'rejected') return 'border-red-500 bg-red-500 text-white';
    if (status === 'partial') return 'border-orange-500 bg-orange-500 text-white';
    return 'border-primary bg-primary text-white';
  }
  return 'border-gray-300 bg-white text-gray-400';
}

export function DeliveryTimeline({ status }: { readonly status: Delivery['status'] }) {
  return (
    <div className="relative">
      <div className="flex items-center justify-between">
        {DELIVERY_STEPS.map((step, index) => {
          const stepStatus = getStepStatus(status, step.id);
          const isLast = index === DELIVERY_STEPS.length - 1;
          const nextStepStatus = isLast
            ? 'upcoming'
            : getStepStatus(status, DELIVERY_STEPS[index + 1].id);

          return (
            <div key={step.id} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                    getStepClassName(stepStatus, status)
                  )}
                >
                  {getStepIcon(stepStatus, status, index)}
                </div>
                <div className="mt-2 text-center">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      stepStatus === 'upcoming' ? 'text-gray-400' : 'text-gray-900'
                    )}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
              </div>
              {isLast ? null : (
                <div className="mx-2 h-0.5 flex-1 self-start mt-5">
                  <div
                    className={cn(
                      'h-full transition-colors',
                      nextStepStatus === 'upcoming' ? 'bg-gray-200' : 'bg-green-500'
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
